import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Fab,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from "../constants/icons";
import SalesProjectSearchFilters from '../components/SalesProjectSearchFilters';
import { ProjectFilters, SortConfig, DEFAULT_FILTERS } from '../hooks/useProjectTable';
import { SELECTABLE_PHASES } from '../hooks/useSfaStructure';
import { DEFAULT_LIST_COLUMNS, SFA_SORT_OPTIONS, isDefaultListColumns, normalizeListColumns } from '../components/sfa/sfaListColumns';
import { PROJECT_TYPE_LABELS } from '../constants/masterData';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import { db } from "../firebase/firebase";
import { collection, query, where, getDocs, deleteDoc, doc, orderBy, onSnapshot } from "firebase/firestore";
import { useAuth } from "../firebase/AuthProvider";
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';

interface SavedFilter {
  id: string;
  name: string;
  filters: any;
  sort: any;
  columns?: string[]; // 統合版一覧の表示列（無い＝今の表示列のまま）
  created_at: any;
}

export default function SalesProjectsFilterList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const notify = useNotification();
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [openSearch, setOpenSearch] = useState(false);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // デフォルトのフィルタとソート
  const defaultFilters: ProjectFilters = DEFAULT_FILTERS;
  const defaultSort: SortConfig = { field: 'phase', order: 'asc' };

  // 🔐 Firestoreから権限を取得
  useEffect(() => {
    if (!user?.email) return;

    const unsub = onSnapshot(
      query(
        collection(db, "HozenallCloud_Permissions"),
        where("user_id", "==", user.email)
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setPermissions(data.permissions || []);
        } else {
          setPermissions([]);
        }
      },
      (error) => {
        console.error("Firestore permissions error:", error);
      }
    );
    return () => unsub();
  }, [user?.email]);

  const viewLevel = !permissions
    ? 'individual'
    : permissions.includes('readdata.sfa.all_view')
      ? 'all'
      : permissions.includes('readdata.sfa.branch_view')
        ? 'branch'
        : 'individual';


  const fetchSavedFilters = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "HozenallCloud_Sfa_SavedFilters"),
        where("user_id", "==", user.email)
      );
      const querySnapshot = await getDocs(q);
      const filters = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SavedFilter[];

      // クライアント側で作成日時順（降順）にソート
      filters.sort((a, b) => {
        const dateA = a.created_at?.toDate?.() || new Date(0);
        const dateB = b.created_at?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setSavedFilters(filters);
    } catch (error) {
      console.error("Error fetching saved filters:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedFilters();
  }, [user?.email]);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await confirm({
      title: '条件の削除',
      message: 'この条件を削除してもよろしいですか？',
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "HozenallCloud_Sfa_SavedFilters", id));
      setSavedFilters(prev => prev.filter(f => f.id !== id));
    } catch (error) {
      console.error("Error deleting filter:", error);
      notify.error("削除に失敗しました。");
    }
  };

  const handleEdit = (filter: SavedFilter, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingFilter(filter);
    setOpenSearch(true);
  };

  // 統合版（/sfa/projects）で開く。移行期間中は旧画面でも開ける
  const handleApply = (filter: SavedFilter, legacy = false) => {
    navigate(legacy ? '/salesprojectsmanagement' : '/sfa/projects', {
      state: {
        appliedFilters: filter?.filters || {},
        appliedSort: filter?.sort || (legacy ? { field: 'phase', order: 'asc' } : { field: 'updated_date', order: 'desc' }),
        appliedFilterName: filter?.name,
        ...(!legacy && filter?.columns ? { appliedColumns: filter.columns } : {}),
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '---';
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleDateString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    return '---';
  };

  return (
    <Box sx={{ p: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[800] }}>
          <Icons.FactCheck /> 保存済み検索条件一覧
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Fab
            variant="extended"
            onClick={() => setOpenSearch(true)}
            sx={[STANDARD_FAB_CTA_SX, { mr: 3 }]}
          >
            <Icons.Add />
            新規条件作成
          </Fab>
          <Fab
            variant="extended"
            onClick={() => navigate('/salesprojectsmanagement')}
            sx={STANDARD_FAB_CTA_SX}
          >
            <Icons.ArrowBack />
            案件リスト
          </Fab>
        </Box>
      </Box>

      {/* 新規条件作成ダイアログ */}
      <Dialog
        open={openSearch}
        onClose={() => {
          setOpenSearch(false);
          setEditingFilter(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle component="div" sx={{ bgcolor: '#f1f5f9', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', fontSize: '14px' }}>
            {editingFilter ? `検索条件の編集: ${editingFilter.name}` : '新規検索条件の作成'}
          </Typography>
          <IconButton size="small" onClick={() => { setOpenSearch(false); setEditingFilter(null); }}>
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <SalesProjectSearchFilters
            filters={editingFilter ? editingFilter.filters : defaultFilters}
            sort={editingFilter ? editingFilter.sort : defaultSort}
            viewLevel={viewLevel}
            phaseOptions={SELECTABLE_PHASES} // 条件は統合版で開くため、フェーズの選択肢も統合版に合わせる (販売前の仕入案件は仕入フェーズで判定)
            sortOptions={SFA_SORT_OPTIONS}
            columns={editingFilter?.columns ? normalizeListColumns(editingFilter.columns) : [...DEFAULT_LIST_COLUMNS]}
            docId={editingFilter?.id}
            initialFilterName={editingFilter?.name}
            onSearch={(newFilters) => {
              setOpenSearch(false);
              navigate('/sfa/projects', {
                state: {
                  appliedFilters: newFilters,
                  appliedSort: { field: 'updated_date', order: 'desc' }
                }
              });
            }}
            onSortChange={() => { }}
            onSaveSuccess={() => {
              fetchSavedFilters();
              setOpenSearch(false);
              setEditingFilter(null);
            }}
            showSearchButton={false}
            showResetButton={false}
          />
        </DialogContent>
      </Dialog>

      {savedFilters.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <Icons.FilterList sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography color="text.secondary">
            保存された検索条件はありません。<br />
            案件一覧の検索画面から条件を保存できます。
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {savedFilters.map((filter) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={filter.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: '0.2s',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)'
                  },
                  position: 'relative'
                }}
                onClick={() => handleApply(filter)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                      {filter.name || '無題の条件'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          onClick={(e) => handleEdit(filter, e)}
                          sx={{ color: '#94a3b8', '&:hover': { color: '#607d8b' } }}
                        >
                          <Icons.Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(filter.id, e)}
                          sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                        >
                          <Icons.Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                    {filter.filters?.project_name && (
                      <Chip label={`KW: ${filter.filters.project_name}`} size="small" variant="outlined" />
                    )}
                    {filter.filters?.phase && filter.filters.phase.length > 0 && (
                      <Chip
                        label={`フェーズ: ${Array.isArray(filter.filters.phase) ? filter.filters.phase.join(', ') : filter.filters.phase}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {filter.filters?.project_type && filter.filters.project_type.length > 0 && (
                      <Chip
                        label={`種別: ${filter.filters.project_type.map((t: string) => PROJECT_TYPE_LABELS[t] ?? t).join(', ')}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {filter.filters?.main_staff_branch_name && filter.filters.main_staff_branch_name.length > 0 && (
                      <Chip
                        label={`支店: ${Array.isArray(filter.filters.main_staff_branch_name) ? filter.filters.main_staff_branch_name.join(', ') : filter.filters.main_staff_branch_name}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#64748b', color: '#64748b' }}
                      />
                    )}
                    {filter.filters?.main_staff_department_name && filter.filters.main_staff_department_name.length > 0 && (
                      <Chip
                        label={`部署: ${Array.isArray(filter.filters.main_staff_department_name) ? filter.filters.main_staff_department_name.join(', ') : filter.filters.main_staff_department_name}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#64748b', color: '#64748b' }}
                      />
                    )}
                    {filter.filters?.main_staff_name && (
                      <Chip label={`メイン担当者: ${filter.filters.main_staff_name}`} size="small" variant="outlined" />
                    )}
                    {filter.filters?.category_large && (
                      <Chip label={filter.filters.category_large} size="small" variant="outlined" />
                    )}
                    {filter.sort && (
                      <Chip
                        label={`ソート: ${SFA_SORT_OPTIONS.find((o) => o.value === filter.sort.field)?.label || filter.sort.field} (${filter.sort.order === 'asc' ? '昇順' : '降順'})`}
                        size="small"
                        sx={{ bgcolor: '#f1f5f9' }}
                      />
                    )}
                    {filter.columns && !isDefaultListColumns(normalizeListColumns(filter.columns)) && (
                      <Chip label={`表示列: ${normalizeListColumns(filter.columns).length} 列`} size="small" sx={{ bgcolor: '#f1f5f9' }} />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      保存日: {formatDate(filter.created_at)}
                    </Typography>
                    <Typography
                      variant="caption" component="button"
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleApply(filter, true); }}
                      sx={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', p: 0, '&:hover': { color: '#607d8b' } }}
                    >
                      旧画面で開く
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
